import { useState, useEffect } from 'react';
import { dashboardApi, llcApi, checkApi, wireApi, bankApi, achApi, payeeAnalyticsApi } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Building2, FileText, Banknote, Clock, CreditCard, Send, ArrowUpDown, ArrowUp, ArrowDown, ArrowRightLeft, Users, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';

export const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [checks, setChecks] = useState([]);
  const [wires, setWires] = useState([]);
  const [achTransfers, setAchTransfers] = useState([]);
  const [llcs, setLlcs] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedLlc, setSelectedLlc] = useState(null);
  const [payeeSpending, setPayeeSpending] = useState([]);
  const [payeeLlcFilter, setPayeeLlcFilter] = useState('all');
  const [payeesPanelOpen, setPayeesPanelOpen] = useState(() => localStorage.getItem('dash_payeesPanel') !== 'false');
  const [showAllPayees, setShowAllPayees] = useState(false);
  const [paymentsPanelOpen, setPaymentsPanelOpen] = useState(() => localStorage.getItem('dash_paymentsPanel') !== 'false');
  const [selectedPayee, setSelectedPayee] = useState(null);
  const [payeePayments, setPayeePayments] = useState([]);
  const [showSensitive, setShowSensitive] = useState(() => localStorage.getItem('dash_showSensitive') !== 'false');

  const togglePayeesPanel = () => {
    setPayeesPanelOpen(prev => { const v = !prev; localStorage.setItem('dash_payeesPanel', v); return v; });
  };
  const togglePaymentsPanel = () => {
    setPaymentsPanelOpen(prev => { const v = !prev; localStorage.setItem('dash_paymentsPanel', v); return v; });
  };
  const toggleSensitive = () => {
    setShowSensitive(prev => { const v = !prev; localStorage.setItem('dash_showSensitive', v); return v; });
  };
  const mask = (val) => showSensitive ? val : '••••••';
  const [llcSortField, setLlcSortField] = useState('company_name');
  const [llcSortDir, setLlcSortDir] = useState('asc');

  const handleLlcSort = (field) => {
    if (llcSortField === field) {
      setLlcSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setLlcSortField(field);
      setLlcSortDir('asc');
    }
  };

  const LlcSortIcon = ({ field }) => {
    if (llcSortField !== field) return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
    return llcSortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-white" />
      : <ArrowDown className="w-3 h-3 text-white" />;
  };

  const sortedLlcs = [...llcs].sort((a, b) => {
    let aVal, bVal;
    if (llcSortField === 'next_check_number') {
      aVal = Number(a[llcSortField]) || 0;
      bVal = Number(b[llcSortField]) || 0;
    } else if (llcSortField === 'last_check') {
      aVal = getLastCheckDate(a.id) || '';
      bVal = getLastCheckDate(b.id) || '';
    } else {
      aVal = String(a[llcSortField] || '').toLowerCase();
      bVal = String(b[llcSortField] || '').toLowerCase();
    }
    if (aVal < bVal) return llcSortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return llcSortDir === 'asc' ? 1 : -1;
    return 0;
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, checksRes, wiresRes, llcsRes, banksRes, achRes, payeeRes] = await Promise.all([
        dashboardApi.getStats(),
        checkApi.list(),
        wireApi.list(),
        llcApi.list(),
        bankApi.list(),
        achApi.list(),
        payeeAnalyticsApi.getSpending(),
      ]);
      setStats(statsRes.data);
      setChecks(checksRes.data);
      // Only include wires marked as "sent"
      setWires(wiresRes.data.filter(w => w.status === 'sent'));
      setAchTransfers(achRes.data);
      setLlcs(llcsRes.data);
      setBanks(banksRes.data);
      setPayeeSpending(payeeRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const openPayeeDetail = (payeeName) => {
    setSelectedPayee(payeeName);
    // Gather all payments for this payee (case-insensitive match)
    const upper = payeeName.toUpperCase();
    const matchChecks = checks.filter(c => (c.payee || '').toUpperCase() === upper && c.status !== 'voided')
      .map(c => ({ type: 'Check', ref: `#${c.check_number}`, date: c.date, amount: c.amount, llc: getLLCName(c.llc_id), memo: c.memo || '' }));
    const matchWires = wires.filter(w => (w.beneficiary_name || '').toUpperCase() === upper)
      .map(w => ({ type: 'Wire', ref: '', date: w.created_at, amount: w.amount, llc: getLLCName(w.llc_id), memo: w.purpose || '' }));
    const matchAch = achTransfers.filter(a => (a.beneficiary_name || '').toUpperCase() === upper && a.status !== 'cancelled')
      .map(a => ({ type: 'ACH', ref: a.reference || '', date: a.effective_date || a.created_at, amount: a.amount, llc: getLLCName(a.llc_id), memo: a.purpose || '' }));
    const all = [...matchChecks, ...matchWires, ...matchAch].sort((a, b) => new Date(b.date) - new Date(a.date));
    setPayeePayments(all);
  };

  const getLLCName = (llcId) => {
    const llc = llcs.find(l => l.id === llcId);
    return llc?.company_name || 'Unknown';
  };

  const getLastCheckDate = (llcId) => {
    const llcChecks = checks.filter(c => c.llc_id === llcId);
    if (llcChecks.length === 0) return null;
    const sorted = llcChecks.sort((a, b) => new Date(b.date) - new Date(a.date));
    return sorted[0]?.date;
  };

  const getBankName = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    return bank ? bank.name : '';
  };

  const getBankRep = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    return bank ? (bank.rep_name || '') : '';
  };

  const getBankRouting = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    return bank ? (bank.routing_number || bank.outbound_routing || bank.inbound_routing || '') : '';
  };

  const formatPhone = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Calculate totals - separate pending from completed
  const pendingChecks = checks.filter(c => c.status === 'pending');
  const completedChecks = checks.filter(c => c.status === 'printed');
  const pendingAch = achTransfers.filter(a => a.status === 'pending');
  const sentAch = achTransfers.filter(a => a.status === 'sent');
  
  const pendingTotal = pendingChecks.reduce((sum, c) => sum + c.amount, 0);
  const completedCheckTotal = completedChecks.reduce((sum, c) => sum + c.amount, 0);
  const wireTotal = wires.reduce((sum, w) => sum + w.amount, 0);
  const achSentTotal = sentAch.reduce((sum, a) => sum + a.amount, 0);
  const completedTotal = completedCheckTotal + wireTotal + achSentTotal;

  // Combine and sort all COMPLETED payments (printed checks + sent wires + sent ACH)
  const allPayments = [
    ...checks.filter(c => c.status === 'printed').map(c => ({ ...c, type: 'check', sortDate: new Date(c.date) })),
    ...wires.filter(w => w.status === 'sent').map(w => ({ ...w, type: 'wire', sortDate: new Date(w.created_at) })),
    ...achTransfers.filter(a => a.status === 'sent').map(a => ({ ...a, type: 'ach', sortDate: new Date(a.effective_date) })),
  ].sort((a, b) => b.sortDate - a.sortDate);

  const filteredPayments = activeTab === 'all' 
    ? allPayments.slice(0, 10)
    : allPayments.filter(p => p.type === activeTab).slice(0, 10);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-500">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-3 fade-in" data-testid="dashboard-page">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Dashboard</h1>
        <p className="text-slate-500 text-xs">Overview of your payment activity</p>
      </div>

      {/* Summary Cards - Same as Payment Register */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {/* Pending Payments */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-amber-700">Pending</p>
                <p className="text-lg font-semibold text-amber-900">{formatCurrency(pendingTotal)}</p>
                <p className="text-xs text-amber-600">{pendingChecks.length} checks awaiting print</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Completed Checks */}
        <Card className="themed-card-border">
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg themed-accent-bg">
                <FileText className="w-4 h-4 themed-accent-icon" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Printed Checks</p>
                <p className="text-lg font-semibold text-slate-900">{formatCurrency(completedCheckTotal)}</p>
                <p className="text-xs text-slate-400">{completedChecks.length} printed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Wire Transfers */}
        <Card className="themed-card-border">
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg themed-accent-bg">
                <Banknote className="w-4 h-4 themed-accent-icon" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Wire Transfers</p>
                <p className="text-lg font-semibold text-slate-900">{formatCurrency(wireTotal)}</p>
                <p className="text-xs text-slate-400">{wires.length} sent</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ACH Transfers */}
        <Card className="themed-card-border" data-testid="ach-stats-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg themed-accent-bg">
                <ArrowRightLeft className="w-4 h-4 themed-accent-icon" />
              </div>
              <div>
                <p className="text-sm text-slate-500">ACH Transfers</p>
                <p className="text-lg font-semibold text-slate-900">{formatCurrency(achSentTotal)}</p>
                <p className="text-xs text-slate-400">{sentAch.length} sent, {pendingAch.length} pending</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Completed */}
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CreditCard className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-green-700">Total Completed</p>
                <p className="text-lg font-semibold text-green-900">{formatCurrency(completedTotal)}</p>
                <p className="text-xs text-green-600">{completedChecks.length + wires.length + sentAch.length} payments</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payee Spending Analytics */}
      <Card className="border-slate-200" data-testid="payee-analytics-card">
        <CardHeader className="rounded-t-lg cursor-pointer panel-header-themed"  onClick={togglePayeesPanel}>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2" style={{ color: '#ffffff' }}>
              <Users className="w-5 h-5" />
              Payee Spending Breakdown
              {payeesPanelOpen ? <ChevronUp className="w-4 h-4 ml-1 opacity-60" /> : <ChevronDown className="w-4 h-4 ml-1 opacity-60" />}
            </span>
            {payeesPanelOpen && (
              <div className="w-48" onClick={(e) => e.stopPropagation()}>
                <Select value={payeeLlcFilter} onValueChange={async (val) => {
                  setPayeeLlcFilter(val);
                  try {
                    const res = await payeeAnalyticsApi.getSpending(val !== 'all' ? val : null);
                    setPayeeSpending(res.data);
                  } catch (e) { console.error(e); }
                }}>
                  <SelectTrigger className="h-8 bg-white text-slate-900 text-sm [&>span]:text-slate-900" data-testid="payee-llc-filter">
                    <SelectValue placeholder="All LLCs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All LLCs</SelectItem>
                    {llcs.map(l => <SelectItem key={l.id} value={l.id}>{l.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        {payeesPanelOpen && (
          <CardContent>
            {payeeSpending.length === 0 ? (
              <div className="text-center py-8 text-slate-400">No payee data yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'center', width: '2rem' }}>#</th>
                      <th>Payee</th>
                      <th style={{ textAlign: 'right' }}>Checks</th>
                      <th style={{ textAlign: 'right' }}>Wires</th>
                      <th style={{ textAlign: 'right' }}>ACH</th>
                      <th style={{ textAlign: 'right' }}>Grand Total</th>
                      <th style={{ textAlign: 'center' }}>Payments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllPayees ? payeeSpending : payeeSpending.slice(0, 10)).map((p, i) => (
                      <tr key={p.name} data-testid={`payee-row-${i}`}>
                        <td style={{ textAlign: 'center' }} className="text-slate-400 text-xs">{i + 1}</td>
                        <td>
                          <button
                            onClick={() => openPayeeDetail(p.name)}
                            className="font-semibold text-left hover:text-blue-600 hover:underline transition-colors cursor-pointer"
                            data-testid={`payee-link-${i}`}
                          >
                            {p.name}
                          </button>
                        </td>
                        <td style={{ textAlign: 'right' }} className="font-mono text-sm">
                          {p.check_total > 0 ? formatCurrency(p.check_total) : <span className="text-slate-300">-</span>}
                        </td>
                        <td style={{ textAlign: 'right' }} className="font-mono text-sm">
                          {p.wire_total > 0 ? formatCurrency(p.wire_total) : <span className="text-slate-300">-</span>}
                        </td>
                        <td style={{ textAlign: 'right' }} className="font-mono text-sm">
                          {p.ach_total > 0 ? formatCurrency(p.ach_total) : <span className="text-slate-300">-</span>}
                        </td>
                        <td style={{ textAlign: 'right' }} className="font-mono font-semibold">{formatCurrency(p.grand_total)}</td>
                        <td style={{ textAlign: 'center' }} className="text-sm text-slate-500">{p.total_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {payeeSpending.length > 10 && (
                  <button
                    onClick={() => setShowAllPayees(p => !p)}
                    className="w-full py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1 transition-colors"
                    data-testid="toggle-all-payees"
                  >
                    {showAllPayees ? (
                      <><ChevronUp className="w-4 h-4" /> Show top 10 only</>
                    ) : (
                      <><ChevronDown className="w-4 h-4" /> Show all {payeeSpending.length} payees</>
                    )}
                  </button>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Recent Payments */}
      <Card className="border-slate-200">
        <CardHeader className="rounded-t-lg cursor-pointer panel-header-themed"  onClick={togglePaymentsPanel}>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2" style={{ color: '#ffffff' }}>
              <CreditCard className="w-5 h-5" />
              Recent Payments
              {paymentsPanelOpen ? <ChevronUp className="w-4 h-4 ml-1 opacity-60" /> : <ChevronDown className="w-4 h-4 ml-1 opacity-60" />}
            </span>
            {paymentsPanelOpen && (
              <div onClick={(e) => e.stopPropagation()}>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
                  <TabsList>
                    <TabsTrigger value="all">All ({allPayments.length})</TabsTrigger>
                    <TabsTrigger value="check">Checks ({checks.filter(c => c.status === 'printed').length})</TabsTrigger>
                    <TabsTrigger value="wire">Wires ({wires.length})</TabsTrigger>
                    <TabsTrigger value="ach">ACH ({sentAch.length})</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        {paymentsPanelOpen && (
        <CardContent>
          {filteredPayments.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p>No payments found</p>
              <p className="text-sm mt-1">Create checks or send wires to see them here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr>
                    <th>LLC</th>
                    <th>Type</th>
                    <th>Ref #</th>
                    <th>Payee/Beneficiary</th>
                    <th>Memo/Purpose</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment) => (
                    <tr 
                      key={payment.id} 
                      className={payment.type === 'check' && payment.status === 'voided' ? 'opacity-50' : ''}
                    >
                      <td className="font-semibold">{getLLCName(payment.llc_id)}</td>
                      <td>
                        {payment.type === 'check' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            <FileText className="w-3 h-3" />
                            Check
                          </span>
                        ) : payment.type === 'wire' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                            <Send className="w-3 h-3" />
                            Wire
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded">
                            <ArrowRightLeft className="w-3 h-3" />
                            ACH
                          </span>
                        )}
                      </td>
                      <td className="font-mono font-semibold">
                        {payment.type === 'check' ? payment.check_number : payment.type === 'wire' ? `W-${payment.id.slice(0, 6)}` : `A-${payment.id.slice(0, 6)}`}
                      </td>
                      <td className="max-w-[200px] truncate">
                        {payment.type === 'check' ? payment.payee : payment.beneficiary_name}
                      </td>
                      <td className="max-w-[150px] truncate text-slate-500">
                        {payment.type === 'check' ? (payment.memo || '-') : (payment.purpose || '-')}
                      </td>
                      <td className={`text-right font-mono ${payment.type === 'check' && payment.status === 'voided' ? 'line-through' : 'amount-negative'}`}>
                        {formatCurrency(payment.amount)}
                      </td>
                      <td>{new Date(payment.type === 'check' ? payment.date : payment.type === 'wire' ? payment.created_at : payment.effective_date).toLocaleDateString()}</td>
                      <td>
                        {payment.type === 'check' ? (
                          <span className={`status-badge status-${payment.status}`}>
                            {payment.status}
                          </span>
                        ) : payment.type === 'wire' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                            <Send className="w-3 h-3" />
                            sent
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded">
                            <ArrowRightLeft className="w-3 h-3" />
                            {payment.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* LLCs List */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {llcs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No LLCs added yet. Add your first LLC to start printing checks.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead className="sticky top-0 z-10">
                  <tr style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>
                    <th className="cursor-pointer select-none" onClick={() => handleLlcSort('company_name')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">LLC Name <LlcSortIcon field="company_name" /></span>
                    </th>
                    <th className="cursor-pointer select-none" onClick={() => handleLlcSort('ein')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">EIN <LlcSortIcon field="ein" /></span>
                    </th>
                    <th className="cursor-pointer select-none" onClick={() => handleLlcSort('bank_name')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">Bank <LlcSortIcon field="bank_name" /></span>
                    </th>
                    <th style={{ color: '#ffffff', borderBottom: 'none' }}>ABA #</th>
                    <th style={{ color: '#ffffff', borderBottom: 'none' }}>Account #</th>
                    <th style={{ color: '#ffffff', borderBottom: 'none' }}>Bank Rep</th>
                    <th className="cursor-pointer select-none" onClick={() => handleLlcSort('next_check_number')} style={{ color: '#ffffff', borderBottom: 'none', textAlign: 'right' }}>
                      <span className="flex items-center justify-end gap-1.5">Next Check <LlcSortIcon field="next_check_number" /></span>
                    </th>
                    <th className="cursor-pointer select-none" onClick={() => handleLlcSort('last_check')} style={{ color: '#ffffff', borderBottom: 'none', textAlign: 'right' }}>
                      <span className="flex items-center justify-end gap-1.5">Last Check <LlcSortIcon field="last_check" /></span>
                    </th>
                    <th style={{ color: '#ffffff', borderBottom: 'none', width: '2.5rem', textAlign: 'center' }}>
                      <button onClick={(e) => { e.stopPropagation(); toggleSensitive(); }} className="p-1 rounded hover:bg-slate-700 transition-colors" title={showSensitive ? 'Hide sensitive data' : 'Show sensitive data'} data-testid="toggle-sensitive-btn">
                        {showSensitive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLlcs.map((llc) => {
                    const lastCheckDate = getLastCheckDate(llc.id);
                    return (
                      <tr key={llc.id} className="cursor-pointer" onClick={() => setSelectedLlc(llc)}>
                        <td className="font-semibold">{llc.company_name}</td>
                        <td className={!showSensitive ? 'text-slate-400' : ''}>{mask(llc.ein || '-')}</td>
                        <td>{llc.bank_id ? getBankName(llc.bank_id) : (llc.bank_name || '-')}</td>
                        <td className={`font-mono ${!showSensitive ? 'text-slate-400' : ''}`}>{mask(llc.bank_id ? getBankRouting(llc.bank_id) : '-')}</td>
                        <td className={`font-mono ${!showSensitive ? 'text-slate-400' : ''}`}>{mask(llc.account_number || '-')}</td>
                        <td>{llc.bank_id ? getBankRep(llc.bank_id) : '-'}</td>
                        <td style={{ textAlign: 'right' }}>{llc.next_check_number}</td>
                        <td style={{ textAlign: 'right' }}>{lastCheckDate ? new Date(lastCheckDate).toLocaleDateString() : '-'}</td>
                        <td></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payee Detail Modal */}
      <Dialog open={!!selectedPayee} onOpenChange={(open) => { if (!open) setSelectedPayee(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="payee-detail-modal">
          <DialogHeader>
            <DialogTitle>{selectedPayee} — {payeePayments.length} Payments ({formatCurrency(payeePayments.reduce((s, p) => s + p.amount, 0))})</DialogTitle>
          </DialogHeader>
          {payeePayments.length === 0 ? (
            <p className="text-slate-400 text-center py-4">No payments found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table w-full text-sm">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Ref #</th>
                    <th>Date</th>
                    <th>LLC</th>
                    <th>Memo / Purpose</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {payeePayments.map((p, i) => (
                    <tr key={i}>
                      <td>
                        <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded ${
                          p.type === 'Check' ? 'text-blue-600 bg-blue-50' :
                          p.type === 'Wire' ? 'text-green-600 bg-green-50' :
                          'text-purple-600 bg-purple-50'
                        }`}>{p.type}</span>
                      </td>
                      <td className="font-mono">{p.ref}</td>
                      <td>{new Date(p.date).toLocaleDateString()}</td>
                      <td className="text-xs">{p.llc}</td>
                      <td className="text-slate-500 text-xs">{p.memo}</td>
                      <td className="text-right font-mono font-semibold">{formatCurrency(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* LLC Detail Modal */}
      <Dialog open={!!selectedLlc} onOpenChange={(open) => !open && setSelectedLlc(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedLlc?.company_name}</DialogTitle>
          </DialogHeader>
          {selectedLlc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <p className="text-slate-500">Address</p>
                  <p className="font-medium">{selectedLlc.address_line1}</p>
                  {selectedLlc.address_line2 && <p className="font-medium">{selectedLlc.address_line2}</p>}
                  <p className="font-medium">{selectedLlc.city}, {selectedLlc.state} {selectedLlc.zip_code}</p>
                </div>
                <div>
                  <p className="text-slate-500">EIN</p>
                  <p className="font-medium">{selectedLlc.ein}</p>
                </div>
                <div>
                  <p className="text-slate-500">Phone</p>
                  <p className="font-medium">{formatPhone(selectedLlc.phone) || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Bank</p>
                  <p className="font-medium">{selectedLlc.bank_id ? getBankName(selectedLlc.bank_id) : (selectedLlc.bank_name || '-')}</p>
                </div>
                <div>
                  <p className="text-slate-500">Account Number</p>
                  <p className="font-medium">{selectedLlc.account_number || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Routing Number</p>
                  <p className="font-medium">{selectedLlc.routing_number || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Next Check Number</p>
                  <p className="font-medium">{selectedLlc.next_check_number}</p>
                </div>
                <div>
                  <p className="text-slate-500">Last Check</p>
                  <p className="font-medium">{getLastCheckDate(selectedLlc.id) ? new Date(getLastCheckDate(selectedLlc.id)).toLocaleDateString() : '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Total Checks</p>
                  <p className="font-medium">{checks.filter(c => c.llc_id === selectedLlc.id).length}</p>
                </div>
                <div>
                  <p className="text-slate-500">Total Wires</p>
                  <p className="font-medium">{wires.filter(w => w.llc_id === selectedLlc.id).length}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
